import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { ColumnId } from 'admin/staffing/components/StaffingTable/StaffingTableColumns'
import StaffingViewModel from 'admin/staffing/components/Staffing/StaffingViewModel'
import WorkspacePosting, {
  WorkspacePostingStatus,
} from 'admin/staffing/WorkspacePosting'
import Icon from 'components/Icon'
import {
  CheckboxCell,
  CheckCell,
  ImageCell,
  TableCell,
  TableRow,
  TextCell,
} from 'components/Table/ClickableTable'
import styles from './StaffingTableRow.sass'
import { capitalizeFirstLetter } from 'utils/string-util'
import { expr } from 'mobx-utils'

interface StaffingTableRowProps {
  vm: StaffingViewModel
  workspacePosting: WorkspacePosting
}

export default observer(function StaffingTableRow({
  vm,
  workspacePosting,
}: StaffingTableRowProps) {
  const searcher = vm.searcher
  return (
    <TableRow
      className={styles.staffingTableRow}
      onClick={() => vm.selectWorkspacePosting(workspacePosting)}
      data-test={`staffing-table-row-${workspacePosting.id}`}
    >
      {vm.columns.map((column) => {
        switch (column.id) {
          case ColumnId.Checkbox:
            return (
              <CheckboxCell
                key={column.id}
                checked={searcher.isSelected(workspacePosting)}
                onChange={() => searcher.togglePosting(workspacePosting)}
                dataTest={`staffing-table-select-${workspacePosting.id}`}
              />
            )
          case ColumnId.Workspace:
            return (
              <ImageCell
                key={column.id}
                title={workspacePosting.workspace.name}
                image={workspacePosting.workspace.branding.logos.icon}
              />
            )
          case ColumnId.OpportunityType:
            return (
              <TextCell key={column.id}>
                {workspacePosting.opportunityTypeTitle}
              </TextCell>
            )
          case ColumnId.Location:
            return (
              <TextCell key={column.id}>{workspacePosting.location}</TextCell>
            )
          case ColumnId.PrimarySoftware:
            return (
              <TextCell key={column.id}>
                {workspacePosting.primarySoftware}
              </TextCell>
            )
          case ColumnId.Backup:
            return (
              <CheckCell
                key={column.id}
                color={'var(--blue400)'}
                checked={workspacePosting.backupFileNeeded}
                alignment="center"
              />
            )
          case ColumnId.SupplementalSoftware:
            return (
              <TextCell key={column.id}>
                {workspacePosting.supplementalSoftware}
              </TextCell>
            )
          case ColumnId.Specialties:
            return (
              <TextCell key={column.id}>
                {workspacePosting.specialties.join(', ')}
              </TextCell>
            )
          case ColumnId.TypeOfWork:
            return (
              <TextCell key={column.id}>
                {workspacePosting.jobTypes
                  .map((type) => capitalizeFirstLetter(type))
                  .join(', ')}
              </TextCell>
            )
          case ColumnId.ProsRequired:
            return (
              <TextCell key={column.id} alignment="center">
                {workspacePosting.capacity}
              </TextCell>
            )
          // case ColumnId.MaxApplicants:
          //   return (
          //     <TextCell key={column.id} alignment="center">
          //       {workspacePosting.maxApplicants}
          //     </TextCell>
          //   )
          case ColumnId.MaxConcurrentWorkspaces:
            return (
              <TextCell key={column.id} alignment="center">
                {workspacePosting.maxAllowedWorkspaces}
              </TextCell>
            )
          case ColumnId.Applicants:
            return (
              <TextCell key={column.id} alignment="center">
                {workspacePosting.applicants.length}
              </TextCell>
            )
          case ColumnId.Status:
            return (
              <StatusCell key={column.id} workspacePosting={workspacePosting} />
            )
          case ColumnId.Action:
            return <ArrowCell key={column.id} />
          default:
            return <TableCell key={column.id} />
        }
      })}
    </TableRow>
  )
})

interface StatusCellProps {
  workspacePosting: WorkspacePosting
  dataTest?: string
}

const StatusCell = observer(function ({
  workspacePosting,
  dataTest,
}: StatusCellProps) {
  const formattedStatus = expr(() => {
    switch (workspacePosting.status) {
      case WorkspacePostingStatus.Active:
        return { text: 'Active', className: styles.statusActive }
      case WorkspacePostingStatus.Cancelled:
        return { text: 'Cancelled', className: styles.statusClosed }
      case WorkspacePostingStatus.Closed:
        return { text: 'Completed', className: styles.statusClosed }
      case WorkspacePostingStatus.Draft:
        return { text: 'Draft', className: styles.statusDraft }
      case WorkspacePostingStatus.InReview:
        return { text: 'In review', className: styles.statusInReview }
      case WorkspacePostingStatus.Null:
        return { text: 'Unspecified' }
    }
  })

  return (
    <TableCell className={cx(styles.statusCell, formattedStatus.className)}>
      <span data-test={dataTest}>{formattedStatus.text}</span>
    </TableCell>
  )
})

const ArrowCell = () => {
  return (
    <TableCell>
      <div className={styles.iconContainer}>
        <Icon name="arrow" size={24} />
      </div>
    </TableCell>
  )
}
